import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Fab } from '@mui/material';
import { DELETE_INSTANCE, RepeatableFieldWrapper, UPDATE_INSTANCE } from '@parsys/frontend_library';
import { useDispatch, useSelector } from 'react-redux';

import { renderFormFields } from './FormFieldsComponent';
import { repeatableInstanceChange, repeatableInstanceRemove } from 'FRONTEND/reducers/formSlice';

/**
 * A button component to add a new child element.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onClick - The click event handler.
 * @param {boolean} props.disabled - Whether the button is disabled.
 * @returns {JSX.Element} - The rendered AddChildButton component.
 */
export const AddChildButton = ({ onClick, disabled }) => {
  return (
    <Fab
      component='div' // The button tag css is overridden by scss files, so we use a div instead
      size='small'
      color='primary'
      onClick={onClick}
      disabled={disabled}
      sx={{ m: 1 }}
    >
      <AddIcon />
    </Fab>
  );
};

/**
 * A button component to delete a child element.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onClick - The click event handler.
 * @param {Object} props.sx - Additional styling for the button.
 * @returns {JSX.Element} - The rendered DeleteChildButton component.
 */
export const DeleteChildButton = ({ onClick, sx }) => {
  return (
    <Fab
      component='div' // The button tag css is overridden by scss files, so we use a div instead
      size='small'
      color='error'
      sx={{ ...sx, flexShrink: 0 }}
      onClick={onClick}
    >
      <DeleteIcon />
    </Fab>
  );
};

/**
 * A component that renders a repeatable field with add and delete functionality.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.fieldToRepeat - The field configuration to repeat.
 * @param {string} props.examType - The type of the exam.
 * @returns {JSX.Element} - The rendered RepeatableField component.
 */
const RepeatableField = ({ fieldToRepeat, examType }) => {
  const dispatch = useDispatch();
  const locale = useSelector(state => state.user.locale);
  const handleChange = (changes) => {
    if (changes.type === UPDATE_INSTANCE) {
      dispatch(repeatableInstanceChange({
        uuid: changes.uuid,
        value: changes.value,
        examType,
        sectionId: fieldToRepeat.id,
        fieldId: changes.id,
      }));
      return;
    }
    if (changes.type === DELETE_INSTANCE) {
      dispatch(repeatableInstanceRemove({
        uuid: changes.uuid,
        examType,
        sectionId: fieldToRepeat.sectionId,
      }));
    }
  };
  return (
    <RepeatableFieldWrapper
      AddButton={AddChildButton}
      DeleteButton={DeleteChildButton}
      min={fieldToRepeat.minRepetitions ?? 1}
      max={fieldToRepeat.maxRepetitions}
      onChange={handleChange}
    >
      {renderFormFields({ field: fieldToRepeat, saveInformation: handleChange, locale })}
    </RepeatableFieldWrapper>
  );
};

export default RepeatableField;
